import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addDays, isSameDay } from 'date-fns';
import { de } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { api } from '../../utils/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import '../BookingsCalendar/calendar.css';

const locales = {
  de: de,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

export function BlockedDatesPage() {
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedBlockedDate, setSelectedBlockedDate] = useState(null);
  const [formData, setFormData] = useState({
    startDate: '',
    endDate: '',
    reason: '',
  });

  useEffect(() => {
    loadBlockedDates();
  }, []);

  const loadBlockedDates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/blocked-dates');
      setBlockedDates(response.blockedDates || []);
    } catch (error) {
      console.error('Error loading blocked dates:', error);
    } finally {
      setLoading(false);
    }
  };

  // Transform blocked dates to calendar events
  const events = useMemo(() => {
    return blockedDates.map(blocked => {
      const startDate = new Date(blocked.start_date);
      const endDate = new Date(blocked.end_date);

      // Multi-day events should end at end of last day
      const calendarEnd = new Date(endDate);
      calendarEnd.setHours(23, 59, 59);

      return {
        id: blocked.id,
        title: blocked.reason || 'Gesperrt',
        start: startDate,
        end: calendarEnd,
        allDay: true,
        resource: blocked,
        style: {
          backgroundColor: '#ef4444', // red for blocked dates
        },
      };
    });
  }, [blockedDates]);

  const handleSelectSlot = ({ start, end }) => {
    // Adjust end date to the actual last day (Calendar gives us day after)
    const adjustedEnd = new Date(end);
    adjustedEnd.setDate(adjustedEnd.getDate() - 1);

    setFormData({
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(adjustedEnd, 'yyyy-MM-dd'),
      reason: '',
    });
    setSelectedBlockedDate(null);
    setShowAddModal(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedBlockedDate(event.resource);
    setFormData({
      startDate: event.resource.start_date,
      endDate: event.resource.end_date,
      reason: event.resource.reason || '',
    });
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setSelectedBlockedDate(null);
    setFormData({ startDate: '', endDate: '', reason: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await api.post('/admin/blocked-dates', formData);
      await loadBlockedDates();
      handleCloseModal();
    } catch (error) {
      console.error('Error adding blocked date:', error);
      alert('Fehler beim Hinzufügen des Sperrdatums');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Möchten Sie dieses Sperrdatum wirklich löschen?')) {
      return;
    }

    try {
      await api.delete(`/admin/blocked-dates/${id}`);
      await loadBlockedDates();
      setSelectedBlockedDate(null);
    } catch (error) {
      console.error('Error deleting blocked date:', error);
      alert('Fehler beim Löschen des Sperrdatums');
    }
  };

  const eventStyleGetter = (event) => {
    return {
      style: event.style,
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Lade Sperrdaten...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sperrdaten</h1>
          <p className="mt-1 text-sm text-gray-500">
            Verwalten Sie gesperrte Zeiträume für Buchungen
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          Sperrdatum hinzufügen
        </Button>
      </div>

      <Card className="p-6">
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Tipp:</strong> Klicken Sie auf einen Tag oder ziehen Sie über mehrere Tage, um einen neuen Sperrzeitraum hinzuzufügen.
            Klicken Sie auf ein bestehendes Sperrdatum, um Details anzuzeigen.
          </p>
        </div>

        <div style={{ height: '700px' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            culture="de"
            selectable
            messages={{
              next: 'Weiter',
              previous: 'Zurück',
              today: 'Heute',
              month: 'Monat',
              week: 'Woche',
              day: 'Tag',
              agenda: 'Agenda',
              date: 'Datum',
              time: 'Zeit',
              event: 'Sperrdatum',
              noEventsInRange: 'Keine Sperrdaten in diesem Zeitraum',
              showMore: (total) => `+ ${total} weitere`,
            }}
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
          />
        </div>
      </Card>

      {/* Add/View Modal */}
      {(showAddModal || selectedBlockedDate) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseModal}>
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedBlockedDate ? 'Sperrdatum Details' : 'Sperrdatum hinzufügen'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {selectedBlockedDate ? (
              // View mode
              <div className="space-y-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">Zeitraum:</span>
                  <p className="text-gray-900">
                    {format(new Date(selectedBlockedDate.start_date), 'dd.MM.yyyy', { locale: de })}
                    {selectedBlockedDate.end_date !== selectedBlockedDate.start_date && (
                      <> - {format(new Date(selectedBlockedDate.end_date), 'dd.MM.yyyy', { locale: de })}</>
                    )}
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Grund:</span>
                  <p className="text-gray-900">{selectedBlockedDate.reason || '-'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Erstellt von:</span>
                  <p className="text-gray-900">{selectedBlockedDate.created_by || '-'}</p>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-500">Erstellt am:</span>
                  <p className="text-gray-900">
                    {format(new Date(selectedBlockedDate.created_at), 'dd.MM.yyyy HH:mm', { locale: de })}
                  </p>
                </div>
                <div className="flex gap-3 mt-6">
                  <Button
                    onClick={() => handleDelete(selectedBlockedDate.id)}
                    className="flex-1 bg-red-600 hover:bg-red-700"
                  >
                    Löschen
                  </Button>
                  <button
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Schließen
                  </button>
                </div>
              </div>
            ) : (
              // Add mode
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Startdatum
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enddatum
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grund (optional)
                  </label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="z.B. Wartung, Urlaub, etc."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <Button type="submit" className="flex-1">
                    Hinzufügen
                  </Button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Abbrechen
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
